import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  PencilIcon,
  CheckIcon,
  XCircleIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Question Item Component
const SortableQuestionItem = ({ question, index, editingQuestionId, editedText, setEditedText, onStartEdit, onCancelEdit, onSaveEdit, onDelete, getQuestionTypeColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`bg-white border border-gray-200 rounded-lg p-4 ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 mt-1 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getQuestionTypeColor(question.question_type)}`}>
                {question.question_type}
              </span>
              <span className="text-xs text-gray-500">
                Skill: {question.skill_assessed}
              </span>
              {question.isTemp && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                  Unsaved
                </span>
              )}
            </div>
            {editingQuestionId === question.id ? (
              <div className="space-y-2">
                <textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  rows={3}
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => onSaveEdit(question.id)}
                    disabled={!editedText.trim()}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
                  >
                    <CheckIcon className="h-3 w-3 mr-1" />
                    Save
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
                  >
                    <XCircleIcon className="h-3 w-3 mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-900">
                {question.question_text || question.question || '[No question text]'}
              </p>
            )}
          </div>
        </div>
        {editingQuestionId !== question.id && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => onStartEdit(question)}
              className="text-gray-400 hover:text-gray-600"
              title="Edit question"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(question.id)}
              className="text-red-400 hover:text-red-600"
              title="Delete question"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const EditInterviewQuestionsModal = ({ interview, onClose }) => {
  const queryClient = useQueryClient();
  const [questions, setQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'technical',
    skill_assessed: ''
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAddedQuestions, setPendingAddedQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch existing questions
  const { data: questionsData, isLoading } = useQuery({
    queryKey: ['interview-questions', interview.id],
    queryFn: () => apiService.getInterviewQuestions(interview.id),
  });

  console.log('EditInterviewQuestionsModal - Raw questionsData:', questionsData);
  console.log('EditInterviewQuestionsModal - questionsData.data:', questionsData?.data);
  console.log('EditInterviewQuestionsModal - Current questions state:', questions);

  // Update questions when data is fetched
  useEffect(() => {
    console.log('useEffect - questionsData changed:', questionsData);
    if (questionsData?.data) {
      console.log('useEffect - Setting questions to:', questionsData.data);
      // Log first question to see structure
      if (questionsData.data.length > 0) {
        console.log('First question structure:', JSON.stringify(questionsData.data[0], null, 2));
      }
      setQuestions(questionsData.data);
    } else if (questionsData && Array.isArray(questionsData)) {
      console.log('useEffect - questionsData is array, setting directly:', questionsData);
      // Log first question to see structure
      if (questionsData.length > 0) {
        console.log('First question structure:', JSON.stringify(questionsData[0], null, 2));
      }
      setQuestions(questionsData);
    }
  }, [questionsData]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(pendingAddedQuestions.length > 0);
  }, [pendingAddedQuestions]);

  // Update question mutation
  const updateQuestionMutation = useMutation({
    mutationFn: ({ questionId, text }) => 
      apiService.updateQuestion(questionId, { question_text: text }),
    onSuccess: () => {
      toast.success('Question updated successfully');
      queryClient.invalidateQueries(['interview-questions', interview.id]);
      setEditingQuestionId(null);
      setEditedText('');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update question');
    }
  });

  // Delete question mutation
  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId) => apiService.deleteQuestion(questionId),
    onSuccess: () => {
      toast.success('Question deleted successfully');
      queryClient.invalidateQueries(['interview-questions', interview.id]);
      // Also invalidate the main interviews list to update question counts
      queryClient.invalidateQueries(['admin-interviews']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete question');
    }
  });

  // Add custom question mutation
  const addQuestionMutation = useMutation({
    mutationFn: (questionData) => 
      apiService.addCustomQuestion(interview.id, questionData),
    onSuccess: () => {
      toast.success('Question added successfully');
      queryClient.invalidateQueries(['interview-questions', interview.id]);
      setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
      setShowAddForm(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add question');
    }
  });

  // Generate AI questions mutation
  const generateAIQuestionsMutation = useMutation({
    mutationFn: () => apiService.generateAIQuestions(interview.id, 3),
    onSuccess: () => {
      toast.success('AI questions generated successfully');
      queryClient.invalidateQueries(['interview-questions', interview.id]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate AI questions');
    }
  });

  // Reorder questions mutation
  const reorderQuestionsMutation = useMutation({
    mutationFn: (questionOrder) => apiService.reorderQuestions(interview.id, questionOrder),
    onSuccess: () => {
      toast.success('Questions reordered successfully');
      queryClient.invalidateQueries(['interview-questions', interview.id]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reorder questions');
    }
  });

  const handleStartEdit = (question) => {
    setEditingQuestionId(question.id);
    setEditedText(question.question_text);
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditedText('');
  };

  const handleSaveEdit = (questionId) => {
    if (editedText.trim()) {
      updateQuestionMutation.mutate({ questionId, text: editedText });
    }
  };

  const handleDeleteQuestion = (questionId) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      // Check if it's a temporary question
      const question = questions.find(q => q.id === questionId);
      if (question && question.isTemp) {
        // Remove from pending questions and local state
        setPendingAddedQuestions(prev => prev.filter(q => q.id !== questionId));
        setQuestions(prev => prev.filter(q => q.id !== questionId));
        toast.success('Question removed');
      } else {
        // Call API to delete saved question
        deleteQuestionMutation.mutate(questionId);
      }
    }
  };

  const handleAddQuestion = () => {
    if (newQuestion.question_text.trim() && newQuestion.skill_assessed.trim()) {
      // Add to pending questions list instead of immediately saving
      const tempQuestion = {
        ...newQuestion,
        id: `temp-${Date.now()}`,
        isTemp: true
      };
      setPendingAddedQuestions([...pendingAddedQuestions, tempQuestion]);
      setQuestions([...questions, tempQuestion]);
      setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
      setShowAddForm(false);
      toast.success('Question added (not saved yet)');
    }
  };

  const handleSaveInterview = async () => {
    setIsSaving(true);
    try {
      // Save all pending added questions
      for (const question of pendingAddedQuestions) {
        await apiService.addCustomQuestion(interview.id, {
          question_text: question.question_text,
          question_type: question.question_type,
          skill_assessed: question.skill_assessed
        });
      }
      
      // Clear pending questions and refresh
      setPendingAddedQuestions([]);
      queryClient.invalidateQueries(['interview-questions', interview.id]);
      toast.success('Interview saved successfully');
    } catch (error) {
      toast.error('Failed to save interview');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleGenerateAIQuestions = async () => {
    setIsGeneratingAI(true);
    try {
      await generateAIQuestionsMutation.mutateAsync();
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = questions.findIndex((question) => question.id === active.id);
      const newIndex = questions.findIndex((question) => question.id === over.id);

      // Only reorder saved questions, not temporary ones
      const hasTemporaryQuestions = questions.some(q => q.isTemp);
      if (hasTemporaryQuestions) {
        toast.error('Please save all questions before reordering');
        return;
      }

      const newQuestions = arrayMove(questions, oldIndex, newIndex);
      setQuestions(newQuestions);

      // Prepare the question order for the API (array of question IDs in the new order)
      const questionOrder = newQuestions.map(question => question.id);

      setIsReordering(true);
      try {
        await reorderQuestionsMutation.mutateAsync(questionOrder);
      } catch (error) {
        // Revert the local state if the API call fails
        setQuestions(questions);
      } finally {
        setIsReordering(false);
      }
    }
  };

  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'technical': return 'bg-blue-100 text-blue-800';
      case 'behavioral': return 'bg-green-100 text-green-800';
      case 'situational': return 'bg-purple-100 text-purple-800';
      case 'custom': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Edit Interview Questions</h3>
            <p className="mt-1 text-sm text-gray-500">{interview.title}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Total Questions: {questions.length}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Question
            </button>
            {interview.job_description_id && (
              <button
                onClick={handleGenerateAIQuestions}
                disabled={isGeneratingAI}
                className="flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                <SparklesIcon className="h-4 w-4 mr-1" />
                {isGeneratingAI ? 'Generating...' : 'Generate AI Questions'}
              </button>
            )}
          </div>
        </div>

        {showAddForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Question Text</label>
                <textarea
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  placeholder="Enter your question..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_type: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="technical">Technical</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="situational">Situational</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Skill Assessed</label>
                  <input
                    type="text"
                    value={newQuestion.skill_assessed}
                    onChange={(e) => setNewQuestion({ ...newQuestion, skill_assessed: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="e.g., Problem Solving"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewQuestion({ question_text: '', question_type: 'technical', skill_assessed: '' });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuestion}
                  disabled={!newQuestion.question_text.trim() || !newQuestion.skill_assessed.trim()}
                  className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  Add Question
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 250px)' }}>
          {isLoading || isReordering ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">
                {isReordering ? 'Reordering questions...' : 'Loading questions...'}
              </p>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No questions found for this interview.</p>
              <p className="text-sm text-gray-400 mt-2">Add questions using the buttons above.</p>
            </div>
          ) : (
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={questions.map(q => q.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {questions.map((question, index) => (
                    <SortableQuestionItem
                      key={question.id}
                      question={question}
                      index={index}
                      editingQuestionId={editingQuestionId}
                      editedText={editedText}
                      setEditedText={setEditedText}
                      onStartEdit={handleStartEdit}
                      onCancelEdit={handleCancelEdit}
                      onSaveEdit={handleSaveEdit}
                      onDelete={handleDeleteQuestion}
                      getQuestionTypeColor={getQuestionTypeColor}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {hasUnsavedChanges && (
              <span className="text-amber-600 font-medium">
                • {pendingAddedQuestions.length} unsaved question{pendingAddedQuestions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={handleSaveInterview}
              disabled={!hasUnsavedChanges || isSaving}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                'Save Interview'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInterviewQuestionsModal;